import React, { useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChalkboardUser,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faUser,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import { APISD } from "../../../../../../../../config/apisd";
import ToastError from "../../../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../../../NotificationToast/ToastSuccess";
import "bulma/css/bulma.css";
import "../../../../../../../../index.css";
import "./TeacherModal.css";

export default function ModalUpdateConfigTeacher({
  show,
  onHide,
  row,
  headerKelas = [],
  gradeLevelId = "",
  teachers = [],
  loadingTeachers = false,
  form2 = {},
  semesterOptions = [],
  fetchParams,
  outputIdUser = "",
  setGuruMap,
  getGuruKey,
  GetResponseData,
}) {
  const [selectedKelasSemester, setSelectedKelasSemester] = useState({});
  const [selectedGuruIds, setSelectedGuruIds] = useState([]);
  const [teacherSearch, setTeacherSearch] = useState("");
  const [savingTeacherConfig, setSavingTeacherConfig] = useState(false);

  const safeString = (value) =>
    value === null || value === undefined ? "" : String(value);

  const normalizeId = (value) => safeString(value).trim();

  const toTitleCase = (value) => {
    const text = safeString(value).toLowerCase().trim();
    if (!text) return "";

    return text.replace(
      /\w\S*/g,
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );
  };

  const toTitleCaseGelar = (value) => {
    const text = safeString(value).toLowerCase().trim();
    if (!text) return "";

    return text
      .split(" ")
      .map((word) => {
        if (word.includes(".")) {
          return word
            .split(".")
            .map((part) =>
              part ? part.charAt(0).toUpperCase() + part.slice(1) : ""
            )
            .join(".");
        }

        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  const getTeacherId = (teacher) =>
    normalizeId(
      teacher?.id ||
        teacher?.user_id ||
        teacher?.teacher_id ||
        teacher?.ysb_teacher_id ||
        ""
    );

  const getTeacherName = (teacher) =>
    teacher?.name ||
    teacher?.full_name ||
    teacher?.teacher_name ||
    teacher?.nama_lengkap ||
    "Pembina";

  const getSemesterNumberFromOption = (semesterOption) =>
    semesterOption?.raw?.semester ||
    semesterOption?.raw?.name_semester ||
    semesterOption?.label?.replace("Semester ", "") ||
    "";

  const getSemesterIdByNumber = (semesterNumber) => {
    const semester = semesterOptions.find(
      (item) =>
        String(getSemesterNumberFromOption(item)) === String(semesterNumber)
    );

    return semester?.id || "";
  };

  const getSemesterData = (matpel, kelasId, semesterNumber) => {
    const targetLevel = (matpel?.grade_levels || []).find(
      (level) =>
        String(level?.ysb_grade_level_id) === String(gradeLevelId)
    );

    const targetKelas = (targetLevel?.kelas || []).find(
      (kelas) => String(kelas?.ysb_kelas_id) === String(kelasId)
    );

    const targetSemesterId = getSemesterIdByNumber(semesterNumber);

    return (targetKelas?.semesters || []).find((item) => {
      const itemSemesterNumber =
        item?.semester || item?.name_semester || item?.label || "";
      const itemSemesterId = item?.ysb_semester_id || item?.id || "";

      return (
        String(itemSemesterNumber) === String(semesterNumber) ||
        String(itemSemesterId) === String(targetSemesterId)
      );
    });
  };

  const getTeachersFromRow = (matpel, kelasId, semesterNumber) => {
    const targetSemester = getSemesterData(matpel, kelasId, semesterNumber);

    return (targetSemester?.teachers || []).filter(
      (teacher) => String(teacher?.state_form ?? 1) === "1"
    );
  };

  const availableKelas = useMemo(() => {
    if (!row) return [];

    // Kelola Guru selalu menampilkan Semester 1 dan Semester 2.
    // Tetap hanya mengambil kelas pada tingkatan dan matpel yang sedang dipilih.
    const semestersToShow = [1, 2];
    const result = [];

    headerKelas.forEach((kelas) => {
      const kelasId = kelas?.ysb_kelas_id || "";
      if (!kelasId) return;

      const titleKelas = kelas?.title || kelasId || "-";
      const finalTitle = String(titleKelas).toLowerCase().startsWith("kelas")
        ? titleKelas
        : `Kelas ${toTitleCase(titleKelas)}`;

      semestersToShow.forEach((semesterNumber) => {
        const semesterData = getSemesterData(row, kelasId, semesterNumber);

        // Tampilkan hanya kelas yang matpelnya memang terdaftar di semester tersebut.
        if (String(semesterData?.state_form) !== "1") return;

        result.push({
          kelasId,
          title: finalTitle,
          semester: semesterNumber,
          currentTeachers: getTeachersFromRow(row, kelasId, semesterNumber),
        });
      });
    });

    return result;
  }, [row, headerKelas, gradeLevelId, semesterOptions]);

  const availableKelasBySemester = useMemo(() => {
    return [1, 2]
      .map((semesterNumber) => ({
        semester: semesterNumber,
        kelas: availableKelas.filter(
          (item) => Number(item.semester) === semesterNumber
        ),
      }))
      .filter((group) => group.kelas.length > 0);
  }, [availableKelas]);

  const teacherLookup = useMemo(() => {
    const lookup = new Map();

    teachers.forEach((teacher) => {
      const teacherId = getTeacherId(teacher);
      if (teacherId) lookup.set(teacherId, teacher);
    });

    availableKelas.forEach((kelas) => {
      kelas.currentTeachers.forEach((teacher) => {
        const teacherId = getTeacherId(teacher);
        if (teacherId && !lookup.has(teacherId)) {
          lookup.set(teacherId, teacher);
        }
      });
    });

    return lookup;
  }, [teachers, availableKelas]);

  const selectedTeachers = useMemo(
    () =>
      selectedGuruIds
        .map((teacherId) => teacherLookup.get(teacherId))
        .filter(Boolean),
    [selectedGuruIds, teacherLookup]
  );

  const filteredTeachers = useMemo(() => {
    const keyword = teacherSearch.trim().toLowerCase();
    const selectedSet = new Set(selectedGuruIds);

    return teachers.filter((teacher) => {
      const teacherId = getTeacherId(teacher);
      if (!teacherId || selectedSet.has(teacherId)) return false;

      if (!keyword) return true;

      const searchText = [
        getTeacherName(teacher),
        teacher?.nik,
        teacher?.nip,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchText.includes(keyword);
    });
  }, [teachers, teacherSearch, selectedGuruIds]);

  const selectedClassCount = Object.values(selectedKelasSemester).filter(
    Boolean
  ).length;

  const resetModalState = () => {
    setSelectedKelasSemester({});
    setSelectedGuruIds([]);
    setTeacherSearch("");
    setSavingTeacherConfig(false);
  };

  const handleClose = () => {
    if (savingTeacherConfig) return;
    resetModalState();
    onHide?.();
  };

  const closeAfterSuccess = () => {
    resetModalState();
    onHide?.();
  };

  const handleEntered = () => {
    const defaultChecked = {};
    const targetKelasId = row?.targetKelasId || "";
    const targetSemester = row?.targetSemester || "";
    const defaultGuruIds = [];

    availableKelas.forEach((item) => {
      const key = `${item.kelasId}__${item.semester}`;
      const isClickedClass =
        String(item.kelasId) === String(targetKelasId) &&
        String(item.semester) === String(targetSemester);

      defaultChecked[key] = isClickedClass;

      if (isClickedClass) {
        item.currentTeachers.forEach((teacher) => {
          const teacherId = getTeacherId(teacher);
          if (teacherId && !defaultGuruIds.includes(teacherId)) {
            defaultGuruIds.push(teacherId);
          }
        });
      }
    });

    setSelectedKelasSemester(defaultChecked);
    setSelectedGuruIds(defaultGuruIds);
    setTeacherSearch("");
  };

  const handleToggleKelasSemester = (item) => {
    if (savingTeacherConfig) return;

    const key = `${item.kelasId}__${item.semester}`;
    setSelectedKelasSemester((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleAddTeacher = (teacher) => {
    if (savingTeacherConfig) return;

    const teacherId = getTeacherId(teacher);
    if (!teacherId) return;

    setSelectedGuruIds((prev) =>
      prev.includes(teacherId) ? prev : [...prev, teacherId]
    );
    setTeacherSearch("");
  };

  const handleRemoveTeacher = (teacherId) => {
    if (savingTeacherConfig) return;

    setSelectedGuruIds((prev) => prev.filter((id) => id !== teacherId));
  };

  const handleUpdateGuru = async () => {
    if (selectedGuruIds.length === 0) {
      ToastError.fire({
        icon: "error",
        title: "Pilih minimal satu pembina",
      });
      return;
    }

    const selectedMatpelId = row?.ysb_matpel_id || row?.id || "";

    if (!selectedMatpelId) {
      ToastError.fire({
        icon: "error",
        title: "ID ekstrakurikuler tidak ditemukan",
      });
      return;
    }

    const availableKelasMap = new Map(
      availableKelas.map((item) => [
        `${item.kelasId}__${item.semester}`,
        item,
      ])
    );

    const items = Object.entries(selectedKelasSemester)
      .filter(([, checked]) => checked)
      .map(([key]) => {
        const [kelasId, semesterNumber] = key.split("__");
        const kelasItem = availableKelasMap.get(key);

        return {
          ysb_kelas_id: kelasId,
          ysb_semester_id: getSemesterIdByNumber(semesterNumber),
          hasCurrentTeacher: (kelasItem?.currentTeachers || []).length > 0,
        };
      })
      .filter((item) => item.ysb_kelas_id && item.ysb_semester_id);

    if (items.length === 0) {
      ToastError.fire({
        icon: "error",
        title: "Pilih minimal satu kelas yang ingin diperbarui",
      });
      return;
    }

    setSavingTeacherConfig(true);

    try {
      const basePayload = {
        ysb_academic_year_id: form2.ysb_semester_id,
        ysb_branch_id: form2.ysb_branch_id,
        ysb_school_id: form2.ysb_school_id,
        ysb_grade_level_id: form2.ysb_grade_level_id,
        ysb_matpel_id: selectedMatpelId,
        ysb_teacher_id: selectedGuruIds,
        user_id: outputIdUser,
      };

      const updateItems = items
        .filter((item) => item.hasCurrentTeacher)
        .map(({ hasCurrentTeacher, ...item }) => item);

      const storeItems = items
        .filter((item) => !item.hasCurrentTeacher)
        .map(({ hasCurrentTeacher, ...item }) => item);

      const requests = [];

      if (updateItems.length > 0) {
        requests.push(
          APISD.put(
            "/api/ekstrakurikuler-assignments/update",
            {
              ...basePayload,
              items: updateItems,
            },
            fetchParams
          )
        );
      }

      if (storeItems.length > 0) {
        requests.push(
          APISD.post(
            "/api/ekstrakurikuler-assignments/store",
            {
              ...basePayload,
              items: storeItems,
            },
            fetchParams
          )
        );
      }

      await Promise.all(requests);

      const nextGuruMap = {};

      items.forEach((item) => {
        const semesterOption = semesterOptions.find(
          (semester) => String(semester?.id) === String(item.ysb_semester_id)
        );
        const semester = getSemesterNumberFromOption(semesterOption);
        const guruKey = getGuruKey?.(row, item.ysb_kelas_id, semester);

        if (guruKey) {
          nextGuruMap[guruKey] = selectedTeachers.map((teacher) => ({
            ...teacher,
            state_form: 1,
          }));
        }
      });

      setGuruMap?.((prev) => ({
        ...prev,
        ...nextGuruMap,
      }));

      ToastSuccess.fire({
        icon: "success",
        title: "Pembina berhasil diperbarui",
      });

      closeAfterSuccess();
      GetResponseData?.();
    } catch (error) {
      console.error("Gagal memperbarui pembina", error);

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Gagal memperbarui pembina",
      });
    } finally {
      setSavingTeacherConfig(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      onEntered={handleEntered}
      onExited={resetModalState}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      dialogClassName="cm-teacher-modal-dialog"
    >
      <div className="cm-teacher-modal-shell">
        <div className="cm-teacher-modal-header">
          <div className="cm-teacher-modal-heading">
            <span className="cm-teacher-modal-heading-icon is-update">
              <FontAwesomeIcon icon={faPenToSquare} />
            </span>
            <div>
              <span className="cm-teacher-modal-eyebrow">Kelola Pembina</span>
              <h5>{row?.name_matpel || "Ekstrakurikuler"}</h5>
              <p>
                Semester 1 & 2 · {availableKelas.length} pilihan kelas terdaftar
              </p>
            </div>
          </div>

          <button
            type="button"
            className="cm-teacher-modal-close"
            onClick={handleClose}
            disabled={savingTeacherConfig}
            aria-label="Tutup modal"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <Modal.Body className="cm-teacher-modal-body">
          <section className="cm-teacher-modal-section">
            <div className="cm-teacher-section-heading">
              <div>
                {/* <span className="cm-teacher-section-number">1</span> */}
                <div>
                  <h5>Pilih kelas yang diperbarui</h5>
                  <p>
                    Pembina terpilih akan diterapkan sekaligus ke semua kelas yang
                    dicentang.
                  </p>
                </div>
              </div>
              <span className="cm-teacher-counter">
                {selectedClassCount} dipilih
              </span>
            </div>

            {availableKelas.length === 0 ? (
              <div className="cm-teacher-empty-box">
                Data kelas yang terdaftar pada Semester 1 atau Semester 2 tidak
                ditemukan.
              </div>
            ) : (
              <div className="cm-teacher-semester-groups">
                {availableKelasBySemester.map((group) => (
                  <div
                    className="cm-teacher-semester-group"
                    key={`semester-${group.semester}`}
                  >
                    <div className="cm-teacher-semester-heading">
                      <div>
                        <strong>Semester {group.semester}</strong>
                        <span>{group.kelas.length} kelas terdaftar</span>
                      </div>
                      <span className="cm-teacher-semester-badge">
                        S{group.semester}
                      </span>
                    </div>

                    <div className="cm-teacher-class-grid">
                      {group.kelas.map((item) => {
                        const key = `${item.kelasId}__${item.semester}`;
                        const isSelected = !!selectedKelasSemester[key];
                        const currentTeacherNames = item.currentTeachers
                          .map((teacher) =>
                            toTitleCaseGelar(getTeacherName(teacher))
                          )
                          .join(", ");

                        return (
                          <button
                            key={key}
                            type="button"
                            className={`cm-teacher-class-card cm-teacher-class-card-update ${
                              isSelected ? "is-selected" : ""
                            }`}
                            onClick={() => handleToggleKelasSemester(item)}
                            disabled={savingTeacherConfig}
                          >
                            <span className="cm-teacher-class-check">
                              {isSelected ? (
                                <FontAwesomeIcon icon={faCheck} />
                              ) : null}
                            </span>
                            <span className="cm-teacher-class-copy">
                              <strong>{item.title}</strong>
                              <small>
                                {currentTeacherNames
                                  ? `Saat ini: ${currentTeacherNames}`
                                  : "Belum ada pembina"}
                              </small>
                            </span>
                            <span
                              className={`cm-teacher-class-status ${
                                item.currentTeachers.length > 0
                                  ? "is-filled"
                                  : "is-open"
                              }`}
                            >
                              {item.currentTeachers.length > 0
                                ? "Terisi"
                                : "Kosong"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="cm-teacher-modal-section">
            <div className="cm-teacher-section-heading">
              <div>
                {/* <span className="cm-teacher-section-number">2</span> */}
                <div>
                  <h5>Atur Pembina</h5>
                  <p>
                    Cari pembina baru atau hapus pembina terpilih melalui tanda silang.
                  </p>
                </div>
              </div>
              <span className="cm-teacher-counter">
                {selectedGuruIds.length} dipilih
              </span>
            </div>

            {selectedTeachers.length > 0 ? (
              <div className="cm-selected-teacher-wrap">
                {selectedTeachers.map((teacher) => {
                  const teacherId = getTeacherId(teacher);

                  return (
                    <span className="cm-selected-teacher-chip" key={teacherId}>
                      <span className="cm-selected-teacher-avatar">
                        {teacher?.photo ? (
                          <img src={teacher.photo} alt={getTeacherName(teacher)} />
                        ) : (
                          <FontAwesomeIcon icon={faUser} />
                        )}
                      </span>
                      <span>{toTitleCaseGelar(getTeacherName(teacher))}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTeacher(teacherId)}
                        disabled={savingTeacherConfig}
                        aria-label={`Hapus ${getTeacherName(teacher)}`}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="cm-teacher-empty-selection">
                Belum ada pembina yang dipilih.
              </div>
            )}

            <div className="cm-teacher-search-box">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <input
                type="text"
                value={teacherSearch}
                onChange={(event) => setTeacherSearch(event.target.value)}
                placeholder="Cari nama pembina atau NIK..."
                disabled={savingTeacherConfig || loadingTeachers}
              />
              {teacherSearch && (
                <button
                  type="button"
                  onClick={() => setTeacherSearch("")}
                  disabled={savingTeacherConfig}
                  aria-label="Hapus pencarian"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
            </div>

            <div className="cm-teacher-result-list">
              {loadingTeachers ? (
                <div className="cm-teacher-loading">
                  <PulseLoader color="#3f5bea" size={9} />
                  <span>Memuat data pembina...</span>
                </div>
              ) : teachers.length === 0 ? (
                <div className="cm-teacher-empty-box">
                  Data pembina tidak ditemukan.
                </div>
              ) : filteredTeachers.length === 0 ? (
                <div className="cm-teacher-empty-box">
                  {teacherSearch
                    ? "Pembina yang dicari tidak ditemukan."
                    : "Semua pembina pada daftar sudah dipilih."}
                </div>
              ) : (
                filteredTeachers.map((teacher) => {
                  const teacherId = getTeacherId(teacher);

                  return (
                    <button
                      type="button"
                      className="cm-teacher-result-item"
                      key={teacherId}
                      onClick={() => handleAddTeacher(teacher)}
                      disabled={savingTeacherConfig}
                    >
                      <span className="cm-teacher-result-avatar">
                        {teacher?.photo ? (
                          <img src={teacher.photo} alt={getTeacherName(teacher)} />
                        ) : (
                          <FontAwesomeIcon icon={faUser} />
                        )}
                      </span>
                      <span className="cm-teacher-result-copy">
                        <strong>
                          {toTitleCaseGelar(getTeacherName(teacher))}
                        </strong>
                        <small>{teacher?.nik || "Pembina aktif"}</small>
                      </span>
                      <span className="cm-teacher-result-add">
                        <FontAwesomeIcon icon={faPlus} />
                        Pilih
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </section>
        </Modal.Body>

        <div className="cm-teacher-modal-footer">
          <div className="cm-teacher-footer-summary">
            <strong>{selectedGuruIds.length} pembina</strong> akan diterapkan ke{" "}
            <strong>{selectedClassCount} kelas</strong>.
          </div>
          <div className="cm-teacher-footer-actions">
            <button
              type="button"
              className="cm-teacher-btn cm-teacher-btn-secondary"
              onClick={handleClose}
              disabled={savingTeacherConfig}
            >
              Batal
            </button>
            <button
              type="button"
              className="cm-teacher-btn cm-teacher-btn-primary"
              onClick={handleUpdateGuru}
              disabled={savingTeacherConfig}
            >
              {savingTeacherConfig ? (
                "Menyimpan..."
              ) : (
                <>
                  <FontAwesomeIcon icon={faChalkboardUser} />
                  Simpan Perubahan
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
